import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Paper,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { HiSearch, HiPlus, HiTrash, HiPencil } from "react-icons/hi";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAdminOrganizationsSearchPaginated,
  useAdminDeleteAssignment,
  useAdminRolesByOrg,
  useAdminCreateAssignment,
  useAllOrgAssignments,
  adminAssignmentKeys,
} from "@/api/hooks/admin.hooks";
import type { OrganizationDto } from "@/api/types/dtos";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";
import type { AssignmentRow } from "@/api/types/dtos/assignment.dto";
import AssignUserModal from "./AssignUserModal";
import RemoveAssignmentDialog from "./RemoveAssignmentDialog";
import ChangeRoleDialog from "./ChangeRoleDialog";

/**
 * Client-side pagination and filtering:
 *
 * The assignments table currently processes ALL assignments client-side.
 * This is acceptable for small-to-medium deployments but will NOT scale
 * to thousands of assignments. When a dedicated server-side assignments
 * endpoint becomes available, replace the client-side filtering/pagination
 * with server-side queries using page/size/search parameters.
 */
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEBOUNCE_MS = 300;

const AssignmentsTable: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState<OrganizationDto | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleDto | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const [removeAssignment, setRemoveAssignment] = useState<AssignmentRow | null>(null);
  const [changeRoleAssignment, setChangeRoleAssignment] = useState<AssignmentRow | null>(null);
  const [changeRoleSelected, setChangeRoleSelected] = useState<RoleDto | null>(null);

  const [orgSearch, setOrgSearch] = useState("");
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState("");
  const { data: orgsPaginatedData, isLoading: orgsSearchLoading } =
    useAdminOrganizationsSearchPaginated({
      page: 0,
      size: 20,
      search: debouncedOrgSearch || undefined,
    });
  const orgSearchOptions: OrganizationDto[] = orgsPaginatedData?.data?.content ?? [];

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedOrgSearch(orgSearch), 300);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  const { data: roleFilterData } = useAdminRolesByOrg(orgFilter?.id || "");
  const roleFilterOptions: RoleDto[] = (roleFilterData?.data ?? []) as RoleDto[];

  const { data: allAssignments = [], isLoading: assignmentsLoading } = useAllOrgAssignments();

  const deleteMutation = useAdminDeleteAssignment();
  const createMutation = useAdminCreateAssignment();

  const { data: changeRoleOrgData } = useAdminRolesByOrg(
    changeRoleAssignment?.organizationId || ""
  );
  const changeRoleOptions: RoleDto[] = (changeRoleOrgData?.data ?? []) as RoleDto[];

  const filteredAssignments = useMemo(() => {
    let result = allAssignments;

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      result = result.filter(
        (a) =>
          a.userName.toLowerCase().includes(term) ||
          a.userLastName.toLowerCase().includes(term) ||
          a.userIdNumber.toLowerCase().includes(term) ||
          a.userEmail.toLowerCase().includes(term) ||
          a.organizationName.toLowerCase().includes(term) ||
          a.roleName.toLowerCase().includes(term)
      );
    }

    if (orgFilter) {
      result = result.filter((a) => a.organizationId === orgFilter.id);
    }

    if (roleFilter) {
      result = result.filter((a) => a.roleId === roleFilter.id);
    }

    return result;
  }, [allAssignments, debouncedSearch, orgFilter, roleFilter]);

  const paginatedAssignments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAssignments.slice(start, start + rowsPerPage);
  }, [filteredAssignments, page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleRemoveConfirm = useCallback(() => {
    if (!removeAssignment) return;
    deleteMutation.mutate(
      {
        organizationId: removeAssignment.organizationId,
        userId: removeAssignment.userId,
        userRoleAssignmentId: removeAssignment.userRoleAssignmentId,
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Asignación removida exitosamente", {
            variant: "success",
          });
          setRemoveAssignment(null);
        },
        onError: () => {
          enqueueSnackbar("Error al remover la asignación", {
            variant: "error",
          });
        },
      }
    );
  }, [removeAssignment, deleteMutation, enqueueSnackbar]);

  const handleChangeRoleConfirm = useCallback(async () => {
    if (!changeRoleAssignment || !changeRoleSelected) return;
    try {
      await deleteMutation.mutateAsync({
        organizationId: changeRoleAssignment.organizationId,
        userId: changeRoleAssignment.userId,
        userRoleAssignmentId: changeRoleAssignment.userRoleAssignmentId,
      });
      await createMutation.mutateAsync({
        userId: changeRoleAssignment.userId,
        roleId: changeRoleSelected.id,
        organizationId: changeRoleAssignment.organizationId,
      });
      enqueueSnackbar("Rol cambiado exitosamente", { variant: "success" });
      setChangeRoleAssignment(null);
      setChangeRoleSelected(null);
    } catch {
      enqueueSnackbar("Error al cambiar el rol", { variant: "error" });
    }
  }, [changeRoleAssignment, changeRoleSelected, deleteMutation, createMutation, enqueueSnackbar]);

  const handleInvalidateAssignments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
  }, [queryClient]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flex: "1 1 auto",
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Buscar asignaciones..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 240, flex: "1 1 240px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <HiSearch size={18} style={{ opacity: 0.5 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Autocomplete
            options={orgSearchOptions}
            getOptionLabel={(option) => option.name || ""}
            value={orgFilter}
            onChange={(_, value) => {
              setOrgFilter(value);
              setRoleFilter(null);
              setPage(0);
            }}
            onInputChange={(_, value) => setOrgSearch(value)}
            loading={orgsSearchLoading}
            size="small"
            sx={{ minWidth: 200, flex: "0 1 200px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organización"
                placeholder="Buscar organización..."
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {orgsSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
          />
          <Autocomplete
            options={roleFilterOptions}
            getOptionLabel={(option) => option.name || ""}
            value={roleFilter}
            onChange={(_, value) => {
              setRoleFilter(value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 160, flex: "0 1 160px" }}
            renderInput={(params) => <TextField {...params} label="Rol" placeholder="Todos" />}
            disabled={!orgFilter}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<HiPlus size={16} />}
          onClick={() => setAssignModalOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Asignar Usuario
        </Button>
      </Box>

      {assignmentsLoading ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Organización</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={150} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={80} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : filteredAssignments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }} variant="outlined">
          <HiSearch size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {allAssignments.length === 0
              ? "No hay asignaciones de usuarios"
              : "No se encontraron resultados"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {allAssignments.length === 0
              ? "Asigne usuarios a organizaciones y roles para comenzar."
              : "Intente ajustar los filtros de búsqueda."}
          </Typography>
          {allAssignments.length === 0 && (
            <Button
              variant="text"
              startIcon={<HiPlus size={14} />}
              onClick={() => setAssignModalOpen(true)}
            >
              Asignar primer usuario
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Organización</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssignments.map((assignment) => (
                  <TableRow
                    key={`${assignment.userId}-${assignment.organizationId}-${assignment.roleId}`}
                    hover
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {[assignment.userName, assignment.userLastName].filter(Boolean).join(" ")}
                        </Typography>
                        {assignment.userEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {assignment.userEmail}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.userIdNumber}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{assignment.organizationName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.roleName}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Cambiar rol">
                          <IconButton
                            size="small"
                            aria-label={`Cambiar rol de ${assignment.userName}`}
                            onClick={() => {
                              setChangeRoleAssignment(assignment);
                              setChangeRoleSelected(null);
                            }}
                          >
                            <HiPencil size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover asignación">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remover asignación de ${assignment.userName}`}
                            onClick={() => setRemoveAssignment(assignment)}
                          >
                            <HiTrash size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredAssignments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </>
      )}

      <AssignUserModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onSuccess={() => {
          setAssignModalOpen(false);
          handleInvalidateAssignments();
        }}
      />

      <RemoveAssignmentDialog
        open={!!removeAssignment}
        userName={
          removeAssignment
            ? [removeAssignment.userName, removeAssignment.userLastName].filter(Boolean).join(" ")
            : ""
        }
        orgName={removeAssignment?.organizationName || ""}
        roleName={removeAssignment?.roleName || ""}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveAssignment(null)}
        loading={deleteMutation.isPending}
      />

      <ChangeRoleDialog
        open={!!changeRoleAssignment}
        userName={
          changeRoleAssignment
            ? [changeRoleAssignment.userName, changeRoleAssignment.userLastName]
                .filter(Boolean)
                .join(" ")
            : ""
        }
        orgName={changeRoleAssignment?.organizationName || ""}
        roles={changeRoleOptions}
        loading={!changeRoleOrgData}
        submitting={deleteMutation.isPending || createMutation.isPending}
        selectedRole={changeRoleSelected}
        onRoleChange={setChangeRoleSelected}
        onConfirm={handleChangeRoleConfirm}
        onCancel={() => {
          setChangeRoleAssignment(null);
          setChangeRoleSelected(null);
        }}
      />
    </Box>
  );
};

export default AssignmentsTable;
